/* Is this customer already on the list?
 *
 * The server is the authority — it refuses the write, and unique indexes sit
 * under that — but the office should not have to send a form to find out. This
 * runs against the customers already loaded and says so while they are still
 * typing, using the same match keys the server uses (@oneshot2pac/shared), so
 * the two cannot disagree about what counts as the same customer. */
import type { CustomerDto, MatchField } from '@oneshot2pac/shared';
import { emailKey, matchFieldList, nameKey, phoneKey } from '@oneshot2pac/shared';

export interface DuplicateMatch {
  customer: CustomerDto;
  /** Which of the three matched — a re-typed customer usually trips more than
   *  one, and the warning names them all. */
  fields: MatchField[];
}

/** Every customer the draft clashes with. `exceptId` is the customer being
 *  edited: a record always matches itself. */
export function findDuplicates(
  customers: CustomerDto[],
  draft: { name?: string; email?: string; phone?: string },
  exceptId?: string | null,
): DuplicateMatch[] {
  const keys = {
    name: nameKey(draft.name),
    email: emailKey(draft.email),
    phone: phoneKey(draft.phone),
  };
  if (!keys.name && !keys.email && !keys.phone) return [];

  const matches: DuplicateMatch[] = [];
  for (const customer of customers) {
    if (customer.id === exceptId) continue;

    const fields: MatchField[] = [];
    if (keys.name && nameKey(customer.name) === keys.name) fields.push('name');
    if (keys.email && emailKey(customer.email) === keys.email) fields.push('email');
    if (keys.phone && phoneKey(customer.phone) === keys.phone) fields.push('phone');

    if (fields.length) matches.push({ customer, fields });
  }
  return matches;
}

/** The warning the form shows, in the same words the server would use if the
 *  form were sent anyway. */
export function duplicateWarning(match: DuplicateMatch): string {
  const detail = match.fields.filter((f) => f !== 'name');
  if (detail.length === 0) {
    return `${match.customer.name} is already on the customer list.`;
  }
  return `${match.customer.name} already has that ${matchFieldList(detail)}.`;
}
